import { Component, type ErrorInfo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, RotateCcw } from "lucide-react";

/**
 * Rede de segurança para erro em render.
 *
 * Sem ela, qualquer exceção durante o render desmonta a árvore inteira do
 * React e o que sobra é uma **tela branca**: sem sidebar, sem mensagem, sem
 * pista nenhuma para quem está usando — e nenhuma para quem vai investigar
 * depois. Foi assim que a importação de contatos apareceu como "tela branca"
 * em vez de "a tela de resultado quebrou".
 *
 * O erro continua indo para o console (é lá que se diagnostica); a diferença
 * é que o app não some junto.
 */

interface Props {
  children: ReactNode;
  /** Rótulo opcional para o console, identificando o trecho protegido. */
  label?: string;
}

interface State {
  error: Error | null;
}

function ErrorFallback({ error }: { error: Error }) {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-xl rounded-xl border border-red-200 bg-red-50 p-6 dark:border-red-900 dark:bg-red-950/40">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-red-800 dark:text-red-200">
            {t("common.errorBoundary.title")}
          </h2>
          <p className="mt-1 text-sm text-red-700 dark:text-red-300">
            {t("common.errorBoundary.description")}
          </p>
          {/* A mensagem técnica fica visível de propósito: é ela que a pessoa
              copia e cola quando abre o chamado. */}
          <pre className="mt-3 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-white/70 p-2 font-mono text-xs text-red-900 dark:bg-black/30 dark:text-red-200">
            {error.message}
          </pre>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            <RotateCcw className="h-4 w-4" />
            {t("common.errorBoundary.reload")}
          </button>
        </div>
      </div>
    </div>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      `[ErrorBoundary${this.props.label ? ` ${this.props.label}` : ""}]`,
      error,
      info.componentStack,
    );
  }

  render() {
    if (this.state.error) {
      return <ErrorFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
